import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { CodeBlock } from '../../components/CodeBlock';
import { InfoBox } from '../../components/InfoBox';
interface CheckoutResponsePageProps {
  onNavigate?: (pageId: string) => void;
}
export function CheckoutResponsePage({
  onNavigate
}: CheckoutResponsePageProps) {
  return <DocPageLayout title="Interpreting the Response" data-id="element-1363">
      <div className="text-sm text-gray-500 mb-6 -mt-8" data-id="element-1364">
        <button onClick={() => onNavigate?.('overview')} className="hover:underline text-orange-600" data-id="element-1365">
          Overview
        </button>{' '}
        &gt;{' '}
        <button onClick={() => onNavigate?.('checkout')} className="hover:underline text-orange-600" data-id="element-1366">
          &lt;&lt;checkout&gt;&gt;
        </button>{' '}
        &gt; Interpreting the Response
      </div>

      <p className="text-gray-700 mb-6" data-id="element-1367">
        When the payer completes the payment process, they are redirected back
        to your site via the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1368">
          returnUrl
        </code>{' '}
        you specified when creating the session. The gateway appends a{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1369">
          resultIndicator
        </code>{' '}
        parameter to the URL.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" data-id="element-1370">
        Verifying the Result
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1371">
        Compare the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1372">
          resultIndicator
        </code>{' '}
        from the redirect URL with the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1373">
          successIndicator
        </code>{' '}
        stored on your server from the session creation step.
      </p>

      <CodeBlock language="JavaScript" title="Client-Side Verification" code={`// Extract resultIndicator from URL
const urlParams = new URLSearchParams(window.location.search);
const resultIndicator = urlParams.get('resultIndicator');

// Compare with stored successIndicator
if (resultIndicator === storedSuccessIndicator) {
  // Payment was successful
  showSuccessMessage();
} else {
  // Payment may have failed
  showErrorMessage();
}`} data-id="element-1374" />

      <InfoBox type="warning" title="Server-Side Verification Required" data-id="element-1375">
        Never rely solely on the client-side redirect to confirm payment
        success. Always verify the transaction result with a server-to-server
        API call to the Retrieve Order endpoint.
      </InfoBox>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1376">
        Retrieve Order
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1377">
        Use the Retrieve Order API to get the definitive transaction result from
        the gateway.
      </p>
      <CodeBlock language="cURL" title="Retrieve Order Request" code={`curl https://test-gateway.mastercard.com/api/rest/version/72/merchant/{merchantId}/order/{orderId} \\
  -u "merchant.{merchantId}:{apiPassword}" \\
  -X GET`} data-id="element-1378" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1379">
        Response Codes
      </h2>
      <div className="overflow-x-auto mb-8" data-id="element-1380">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1381">
          <thead data-id="element-1382">
            <tr className="bg-gray-50" data-id="element-1383">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1384">
                Result
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1385">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-1386">
            <tr data-id="element-1387">
              <td className="px-4 py-3 font-mono text-xs text-green-600" data-id="element-1388">
                SUCCESS
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1389">
                Transaction completed successfully
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1390">
              <td className="px-4 py-3 font-mono text-xs text-red-600" data-id="element-1391">
                FAILURE
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1392">
                Transaction was declined or failed
              </td>
            </tr>
            <tr data-id="element-1393">
              <td className="px-4 py-3 font-mono text-xs text-amber-600" data-id="element-1394">
                PENDING
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1395">
                Transaction is awaiting processing
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1396">
              <td className="px-4 py-3 font-mono text-xs text-gray-600" data-id="element-1397">
                UNKNOWN
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1398">
                Result could not be determined
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </DocPageLayout>;
}